import { Integer } from '@type-r/ext-types';
import { attributes, AttributesMixin, Collection, define, Model } from '@type-r/models';
import { asCurrency, asTimelessDate, DateRangeFilter, filterByMultiSelect, MultiSelectFilter, TextFilter } from 'lqx-react';
import { CompanyModel, globalStore, RelationshipModel } from 'api';
import { createdUpdatedAttrs, matchString } from 'api/common';
import { fetchJson, restfulIO } from 'api/endpoint';
import { root } from './common';

export const CompanyInfo = attributes({
    name : '',
    account : '',
    bankName : ''
})

export type CompanyInfo = InstanceType<typeof CompanyInfo>

@define
export class UnresolvedPayment extends Model {
    static endpoint = restfulIO( root + 'unresolvedPayments');

    static attributes = {
        ...createdUpdatedAttrs,
        typeId : Integer,
        currency : String,
        amount : Number,
        statementDate : Date,
        valueDate : Date,
        
        customer : CompanyInfo,
        supplier : CompanyInfo,

        remittanceInfoUnstructured : String,
        additionalEntryInfo : String,
        payorAgentId : Integer,
        currentType : String,
    }

    get creditorName() : string {
        return this.supplier ? this.supplier.name : '';
    }

    get debtorName() : string {
        return this.customer ? this.customer.name : '';
    }

    get age() {
        return Math.floor((Date.now() - this.valueDate.getTime()) / 86400000);
    }

    get payorAgent() : CompanyModel {
        return globalStore.companies.get(this.payorAgentId as any);
    }

    filter(filter: UnresolvedPaymentFilters, category: string): boolean {
        return (
            category === 'all' ||
            (category === 'ar' && this.currentType.toLowerCase() === 'ar') ||
            (category === 'nonAr' && this.currentType.toLowerCase() !== 'ar')
        ) &&
            (!filter.filterAll
            || matchString( this.debtorName, filter.filterAll )
            || matchString( this.creditorName, filter.filterAll )
            || matchString( this.customer.account, filter.filterAll )
            || matchString( this.supplier.account, filter.filterAll )
            || matchString( this.amount.toString(), filter.filterAll )
            || matchString( asTimelessDate(this.statementDate), filter.filterAll )
            || matchString( asTimelessDate(this.valueDate), filter.filterAll )
        ) && (!filter.statementDate.from || !filter.statementDate.to || (this.statementDate >= filter.statementDate.from && this.statementDate <= filter.statementDate.to))
        && (!filter.valueDate.from || !filter.valueDate.to || (this.valueDate >= filter.valueDate.from && this.valueDate <= filter.valueDate.to))
        && filterByMultiSelect(filter.debtorName, this.debtorName)
        && filterByMultiSelect(filter.creditorName, this.creditorName)
        && filterByMultiSelect(filter.creditorBankAccount, this.supplier.account)
        && filterByMultiSelect(filter.debtorBankAccount, this.customer.account)
        && (!filter.transactionAmount.value || matchString(asCurrency(this.amount), filter.transactionAmount.value) || matchString(String(this.amount), filter.transactionAmount.value));
    }

    async resolveAll( customerId, supplierId, paymentId : string ) : Promise<number> {

        return await fetchJson( 'POST', root + 'unresolvedPayments/resolve', {
            customer : { ...this.customer.toJSON(), id: customerId },
            supplier : { ...this.supplier.toJSON(), id: supplierId },
            paymentId : Number( paymentId )
        } );
    }
}

export interface UnresolvedPayment extends AttributesMixin<typeof UnresolvedPayment>{}

export interface UnresolvedPaymentFilters extends AttributesMixin<typeof UnresolvedPaymentFilters> {}
@define
export class UnresolvedPaymentFilters extends Model {
    static attributes = {
        filterAll : '',
        statementDate : DateRangeFilter,
        valueDate : DateRangeFilter,
        debtorName: MultiSelectFilter,
        creditorName: MultiSelectFilter,
        creditorBankAccount: MultiSelectFilter,
        debtorBankAccount: MultiSelectFilter,
        transactionAmount: TextFilter,
    }

    initializeOptions(payments: Collection<UnresolvedPayment>) {
        const debtors = payments.map(payment => payment.debtorName);
        this.debtorName.set({options: Array.from(new Set(debtors.filter(x => x)))});

        const creditors = payments.map(payment => payment.creditorName);
        this.creditorName.set({options: Array.from(new Set(creditors.filter(x => x)))});

        const debtorBankAccounts = payments.map(payment => payment.customer.account);
        this.debtorBankAccount.set({options: Array.from(new Set(debtorBankAccounts.filter(x => x)))});

        const creditorBankAccounts = payments.map(payment => payment.supplier.account);
        this.creditorBankAccount.set({options: Array.from(new Set(creditorBankAccounts.filter(x => x)))});
    }
}

export async function getAvailableCompanies(paymentId : string, invoiceNumber: string = undefined) : Promise<Collection<CompanyModel>> {
    const invoiceNumberParam = invoiceNumber ? `?invoiceNumber=${invoiceNumber}` : '';
    return await fetchJson('GET', root + `unresolvedPayments/${paymentId}/available-companies${invoiceNumberParam}`);
}